/**
 * Chat list read-receipt helpers — same rules as ChatMessageComponent ticks:
 * agent + public message → green when readBy has entries (or both arrays empty),
 * otherwise grey; no tick for visitor / private / unread-badge rows.
 *
 * Archived room list snapshots often omit readBy/unreadBy even when the full
 * thread messages are marked read — callers can pass `closedChat: true` so
 * missing receipt fields still render green for the latest agent reply.
 */

function asIdArray(value) {
  if (Array.isArray(value)) return value;
  return null;
}

/** Normalize camelCase / snake_case receipt arrays from list or thread payloads. */
export function getMessageReadReceiptArrays(message) {
  if (!message || typeof message !== "object") {
    return { readBy: null, unreadBy: null };
  }
  return {
    readBy: asIdArray(message.readBy ?? message.read_by),
    unreadBy: asIdArray(message.unreadBy ?? message.unread_by),
  };
}

export function hasReadReceiptFields(message) {
  const { readBy, unreadBy } = getMessageReadReceiptArrays(message);
  return readBy != null || unreadBy != null;
}

export function isAgentMessageRead(message) {
  if (!message || typeof message !== "object") return false;
  const { readBy, unreadBy } = getMessageReadReceiptArrays(message);
  return (
    (Array.isArray(readBy) && readBy.length > 0) ||
    (Array.isArray(readBy) &&
      readBy.length === 0 &&
      Array.isArray(unreadBy) &&
      unreadBy.length === 0)
  );
}

/**
 * Prefer the payload that actually carries receipt arrays (full latestMessage
 * often has them; latestMessageSnapshot on archived lists often does not).
 */
export function pickLatestMessageForReadReceipt(roomOrMessages) {
  if (!roomOrMessages || typeof roomOrMessages !== "object") return null;

  // Direct message object
  if (
    roomOrMessages.isAgent != null ||
    roomOrMessages.content != null ||
    hasReadReceiptFields(roomOrMessages)
  ) {
    if (!roomOrMessages.latestMessageSnapshot && !roomOrMessages.latestMessage) {
      return roomOrMessages;
    }
  }

  const snap = roomOrMessages.latestMessageSnapshot;
  const latest =
    roomOrMessages.latestMessage &&
    typeof roomOrMessages.latestMessage === "object" &&
    !Array.isArray(roomOrMessages.latestMessage)
      ? roomOrMessages.latestMessage
      : null;

  if (hasReadReceiptFields(latest)) return latest;
  if (hasReadReceiptFields(snap)) return snap;
  if (latest) return latest;
  if (snap && typeof snap === "object") return snap;
  return null;
}

/**
 * @param {object|null} latestMessage
 * @param {number} unreadCount
 * @param {{ closedChat?: boolean }} [options]
 * @returns {'read' | 'sent' | null}
 */
export function getChatListReadReceipt(latestMessage, unreadCount = 0, options = {}) {
  if ((parseInt(unreadCount, 10) || 0) > 0) return null;
  if (!latestMessage?.isAgent || latestMessage?.isPrivate) return null;

  if (isAgentMessageRead(latestMessage)) return "read";

  // Closed/archived list rows: snapshot without receipt fields still shows grey
  // while ChatMessageComponent (full messages) shows green — treat missing
  // receipt data on closed chats as read to match the thread.
  if (options.closedChat && !hasReadReceiptFields(latestMessage)) {
    return "read";
  }

  return "sent";
}

/**
 * @returns {'read' | 'sent' | null}
 */
export function getChatListReadReceiptFromMessage(message) {
  return getChatListReadReceipt(message, 0);
}

/**
 * @returns {'read' | 'sent' | null}
 */
export function getChatListReadReceiptFromRoom(room, unreadCount = 0, options = {}) {
  return getChatListReadReceipt(
    pickLatestMessageForReadReceipt(room),
    unreadCount,
    options
  );
}
